import { useEffect, useRef } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useProductSelection } from '@/hooks/useProductSelection';
import { useAuth } from '@/hooks/useAuth';
import { useOrganizationContext } from '@/contexts/OrganizationContext';
import { Loader2 } from 'lucide-react';

interface ProductSelectionGuardProps {
  children?: React.ReactNode;
}

/**
 * Componente que redireciona para a tela de seleção de produtos
 * se a organização ainda não escolheu produtos
 * IMPORTANTE: Só redireciona DEPOIS que a primeira organização for criada
 */
// Rotas em que NÃO redirecionar para product-selection (evitar sair ao atualizar/verificar)
const SKIP_PRODUCT_SELECTION_REDIRECT_PATHS = ['/instances', '/contacts', '/calendar', '/flows', '/prompt-builder', '/documents', '/webchat-lab', '/billing', '/profile', '/settings', '/checkout'];

export function ProductSelectionGuard({ children }: ProductSelectionGuardProps) {
  // IMPORTANTE: Todos os hooks devem ser chamados na mesma ordem sempre
  const navigate = useNavigate();
  const location = useLocation();
  const navigateRef = useRef(navigate);
  const { user, loading: authLoading } = useAuth();
  const { currentOrg, loading: orgLoading } = useOrganizationContext();
  const { needsProductSelection, isLoading } = useProductSelection();

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  // Bloqueio de loja desativado: não redirecionar mais para product-selection ao entrar na aplicação.
  // A rota /product-selection continua disponível para acesso manual (ex.: pelo menu).
  // useEffect(() => {
  //   if (!user) return;
  //   if (authLoading || isLoading || orgLoading) return;
  //   if (!currentOrg?.id) return;
  //   if (needsProductSelection) {
  //     const currentPath = location.pathname;
  //     if (SKIP_PRODUCT_SELECTION_REDIRECT_PATHS.some((p) => currentPath.startsWith(p))) return;
  //     if (currentPath !== '/product-selection') {
  //       navigateRef.current('/product-selection', { replace: true });
  //     }
  //   }
  // }, [needsProductSelection, isLoading, authLoading, orgLoading, user?.id, currentOrg?.id, location.pathname]);

  // Mostrar loading apenas se estiver autenticado e ainda carregando
  // Se não estiver autenticado, renderizar normalmente para permitir acesso ao signup/login
  if (user && (authLoading || isLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return children ? <>{children}</> : <Outlet />;
}
